import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { NavLink } from 'react-router-dom';
import Pagination from '@material-ui/lab/Pagination';
import { Slider } from '@material-ui/core';
import './ProductsTextil.scss';

const ProductsTextil = ({
  category,
  categories,
  checked,
  filterItems,
  handleCheckboxChange,
  handleInputRangeChange,
  handlePaginationChange,
  handleSubCategory,
  inputRangeValue,
  items,
  loading,
  maxPrice,
  minimumPurchase,
  name,
  orderProducts,
  page,
  productsLoading,
  renderItem,
  setOrderProducts,
  subCategories,
  subCategory,
  total,
  url,
  client,
}) => {
  const [filtersOpen, setFiltersOpen] = useState(false);

  const cantMedidasMax = Math.max(
    ...items.map((item) => {
      const descripcionesFiltradas = item.stock_product_descriptions?.filter(
        (desc) => desc.stock_descriptions?.stock_category_id === 2
      ) || [];
      const nombresUnicos = new Set(
        descripcionesFiltradas.map((desc) => desc.stock_descriptions?.name)
      );

      return nombresUnicos.size;
    })
  );

  const renderCategoryChips = () => (
    <div className="chips filter-chip-rail" aria-label="Categorías">
      <NavLink exact to={url} className="chip" activeClassName="active">
        Todas
      </NavLink>
      {categories.map((cat) => (
        <NavLink
          key={cat.id}
          to={`${url}/${cat.code}`}
          className="chip"
          activeClassName="active"
        >
          {cat.name}
        </NavLink>
      ))}
    </div>
  );

  const renderSubcategoryChips = () => (
    !!category && (
      <div className="chips filter-chip-rail" aria-label="Subcategorías">
        <NavLink
          exact
          to={`${url}/${category.code}`}
          className="chip"
          activeClassName="active"
          onClick={() => handleSubCategory()}
        >
          Todas
        </NavLink>

        {subCategories.map((sub) => (
          <NavLink
            key={sub.id}
            to={`${url}/${category.code}/${sub.code}`}
            className="chip"
            activeClassName="active"
            onClick={() => handleSubCategory(sub)}
          >
            {sub.name}
          </NavLink>
        ))}
      </div>
    )
  );

  const renderFiltersBar = () => (
    <div className={`filters-bar ${filtersOpen ? 'open' : ''}`}>
      <div className="filters-row">
        <div className="filters-group">
          <span className="filters-label">Categorías</span>
          {renderCategoryChips()}
        </div>

        {category && (
          <div className="filters-group">
            <span className="filters-label">Subcategorías</span>
            {renderSubcategoryChips()}
          </div>
        )}

        <div className="filters-group slider-group">
          <span className="filters-label">
            Precio: ${inputRangeValue[0].toLocaleString('de-DE')} - ${inputRangeValue[1].toLocaleString('de-DE')}
          </span>
          <Slider
            value={inputRangeValue}
            onChange={handleInputRangeChange}
            aria-labelledby="range-slider"
            min={0}
            max={maxPrice}
          />
        </div>

        <div className="filters-group inline">
          <label className="checkbox">
            <input type="checkbox" checked={checked} onChange={handleCheckboxChange} />
            <span>Solo ofertas</span>
          </label>
        </div>

        <div className="filters-group inline">
          <select
            className="order-select"
            value={orderProducts?.value || ''}
            onChange={(e) =>
              setOrderProducts({ value: e.target.value, label: e.target.value })
            }
          >
            <option value="">Predeterminado</option>
            <option value="ascendente">Precio ascendente</option>
            <option value="descendente">Precio descendente</option>
            <option value="A-Z">A-Z</option>
            <option value="Z-A">Z-A</option>
          </select>
        </div>

        <div className="filters-group inline">
          <button className="apply-button" onClick={filterItems}>Filtrar</button>
        </div>

        <button
          className="filters-toggle"
          onClick={() => setFiltersOpen((v) => !v)}
          aria-label="Mostrar/ocultar filtros"
        >
          Filtros
        </button>
      </div>
    </div>
  );

  const renderPagination = () => (
    <div className="pagination-wrapper-textil" data-testid="pagination">
      <Pagination
        count={total}
        page={page}
        onChange={handlePaginationChange}
        variant="outlined"
        shape="rounded"
      />
    </div>
  );

  const renderItemsWithPagination = () => (
    <>
      {renderPagination()}
      <div className="products-grid" data-testid="paginator">
        {items.map((item) => renderItem(item, cantMedidasMax))}
      </div>
      {renderPagination()}
    </>
  );

  return (
    <div className="products-textil" data-testid="products-page">
      <header className="products-header">
        <h1 className="products-title">{name}</h1>
      </header>

      {renderFiltersBar()}

      {loading && <p className="loading">Cargando productos...</p>}

      {!loading && items.length > 0 && renderItemsWithPagination()}
      {!loading && items.length === 0 && productsLoading && (
        <p className="no-items">No se encontraron productos</p>
      )}
      {!loading && items.length === 0 && !productsLoading && (
        <p className="loading">Cargando...</p>
      )}
    </div>
  );
};

ProductsTextil.propTypes = {
  category: PropTypes.shape({ code: PropTypes.string }),
  categories: PropTypes.array.isRequired,
  checked: PropTypes.bool.isRequired,
  filterItems: PropTypes.func.isRequired,
  handleCheckboxChange: PropTypes.func.isRequired,
  handleInputRangeChange: PropTypes.func.isRequired,
  handlePaginationChange: PropTypes.func.isRequired,
  handleSubCategory: PropTypes.func.isRequired,
  inputRangeValue: PropTypes.arrayOf(PropTypes.number).isRequired,
  items: PropTypes.array.isRequired,
  loading: PropTypes.bool.isRequired,
  maxPrice: PropTypes.number.isRequired,
  minimumPurchase: PropTypes.number,
  name: PropTypes.string.isRequired,
  orderProducts: PropTypes.shape({ value: PropTypes.string, label: PropTypes.string }),
  page: PropTypes.number.isRequired,
  productsLoading: PropTypes.bool,
  renderItem: PropTypes.func.isRequired,
  setOrderProducts: PropTypes.func.isRequired,
  subCategories: PropTypes.array.isRequired,
  subCategory: PropTypes.object,
  total: PropTypes.number.isRequired,
  url: PropTypes.string.isRequired,
  client: PropTypes.object,
};

ProductsTextil.defaultProps = {
  category: null,
  orderProducts: null,
  productsLoading: false,
  minimumPurchase: 0,
  subCategory: null,
  client: {},
};

export default ProductsTextil;
